import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';

interface Setting {
  key: string;
  value: string;
  label: string;
  description?: string;
  type?: 'text' | 'toggle' | 'url' | 'email';
}

const DEFAULT_SETTINGS: Setting[] = [
  { key: 'site_name', value: 'API MART', label: 'Site Name', description: 'The name displayed across the platform', type: 'text' },
  { key: 'site_tagline', value: 'ONE API. INFINITE TRANSACTIONS. ZERO FRICTION.', label: 'Tagline', description: 'Hero section tagline', type: 'text' },
  { key: 'base_url', value: 'https://apimarts.com', label: 'Base URL', description: "Your platform's public URL", type: 'url' },
  { key: 'support_email', value: 'support@apimarts.com', label: 'Support Email', description: 'Email shown to users for support', type: 'email' },
  { key: 'maintenance_mode', value: 'false', label: 'Maintenance Mode', description: 'Show maintenance page to all users', type: 'toggle' },
  { key: 'registration_open', value: 'true', label: 'Open Registration', description: 'Allow new users to register', type: 'toggle' },
];

export default function SettingsTab() {
  const [settings, setSettings] = useState<Setting[]>(DEFAULT_SETTINGS);
  const [editKey, setEditKey] = useState<string | null>(null);
  const [editVal, setEditVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [dbLoading, setDbLoading] = useState(true);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  // Load settings from Supabase site_settings table
  const loadSettings = useCallback(async () => {
    setDbLoading(true);
    try {
      const keys = DEFAULT_SETTINGS.map(s => s.key);
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value')
        .in('key', keys);

      if (!error && data && data.length > 0) {
        const dbMap: Record<string, string> = {};
        data.forEach((row: { key: string; value: string }) => {
          dbMap[row.key] = row.value ?? '';
        });
        setSettings(prev => prev.map(s => ({
          ...s,
          value: dbMap[s.key] !== undefined ? dbMap[s.key] : s.value,
        })));
      }
    } catch {
      // Use defaults if DB unavailable
    }
    setDbLoading(false);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Save a single setting to Supabase
  const saveSetting = async (key: string) => {
    setSaving(true);
    try {
      const updated = settings.map(s => s.key === key ? { ...s, value: editVal } : s);
      setSettings(updated);

      const { error } = await supabase
        .from('site_settings')
        .upsert(
          { key, value: editVal, updated_at: new Date().toISOString() },
          { onConflict: 'key' }
        );

      if (error) {
        showToast('error', `Failed to save: ${error.message}`);
        // Revert on error
        setSettings(prev => prev.map(s => s.key === key ? { ...s, value: s.value } : s));
      } else {
        showToast('success', 'Setting saved');
      }
    } catch {
      showToast('error', 'Failed to save. Check your connection.');
    }
    setEditKey(null);
    setSaving(false);
  };

  // Toggle boolean setting and persist to Supabase
  const toggleSetting = async (key: string) => {
    const current = settings.find(s => s.key === key);
    if (!current) return;
    const newVal = current.value === 'true' ? 'false' : 'true';

    setSettings(prev => prev.map(s => s.key === key ? { ...s, value: newVal } : s));

    try {
      const { error } = await supabase
        .from('site_settings')
        .upsert(
          { key, value: newVal, updated_at: new Date().toISOString() },
          { onConflict: 'key' }
        );

      if (error) {
        // Revert on error
        setSettings(prev => prev.map(s => s.key === key ? { ...s, value: current.value } : s));
        showToast('error', `Failed to update: ${error.message}`);
      } else {
        showToast('success', 'Setting updated');
      }
    } catch {
      setSettings(prev => prev.map(s => s.key === key ? { ...s, value: current.value } : s));
      showToast('error', 'Failed to update. Check your connection.');
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm ${
            toast.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-100'
              : 'bg-red-50 text-red-600 border border-red-100'
          }`}
        >
          <i className={toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />
          {toast.text}
        </motion.div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2 text-sm text-amber-700">
        <i className="ri-information-line mt-0.5 shrink-0" />
        <span>
          Platform settings control the appearance and behavior of your API MART platform.
          Payment provider settings are managed in the <strong>Payments</strong> tab.
          Branding, SEO, and legal pages are managed under <strong>Site Settings</strong>.
        </span>
      </div>

      <div className="bg-white rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-3 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Platform Settings</h3>
            <p className="text-xs text-gray-400 mt-0.5">Global configuration — changes are saved instantly to the database</p>
          </div>
          {dbLoading && (
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <i className="ri-loader-4-line animate-spin" />Loading…
            </div>
          )}
        </div>

        <div className="divide-y divide-surface-3">
          {settings.map((s) => (
            <div key={s.key} className="flex items-center gap-4 px-5 py-4">
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-800">{s.label}</div>
                {s.description && <div className="text-xs text-gray-400 mt-0.5">{s.description}</div>}
              </div>

              {s.type === 'toggle' ? (
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium ${s.value === 'true' ? 'text-green-600' : 'text-gray-400'}`}>
                    {s.value === 'true' ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    onClick={() => toggleSetting(s.key)}
                    disabled={dbLoading}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer disabled:opacity-50 ${
                      s.value === 'true' ? 'bg-brand-500' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                      s.value === 'true' ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              ) : editKey === s.key ? (
                <div className="flex items-center gap-2">
                  <input
                    type={s.type === 'email' ? 'email' : s.type === 'url' ? 'url' : 'text'}
                    className="text-sm border border-surface-3 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-400 w-56"
                    value={editVal}
                    onChange={e => setEditVal(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && saveSetting(s.key)}
                    autoFocus
                  />
                  <button
                    onClick={() => saveSetting(s.key)}
                    disabled={saving}
                    className="text-xs px-3 py-1.5 bg-brand-500 text-white rounded-lg cursor-pointer whitespace-nowrap hover:bg-brand-600 disabled:opacity-50"
                  >
                    {saving ? <i className="ri-loader-4-line animate-spin" /> : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditKey(null)}
                    className="text-xs px-3 py-1.5 border border-surface-3 rounded-lg cursor-pointer whitespace-nowrap text-gray-500 hover:bg-surface-2"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 font-mono max-w-[200px] truncate">{s.value}</span>
                  <button
                    onClick={() => { setEditKey(s.key); setEditVal(s.value); }}
                    disabled={dbLoading}
                    className="text-xs px-3 py-1.5 border border-surface-3 rounded-lg cursor-pointer whitespace-nowrap text-gray-500 hover:bg-surface-2 disabled:opacity-50"
                  >
                    <i className="ri-edit-line mr-1" />Edit
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
